import API, { ensurePrefixed } from 'utils/api';
import getFormattedValues, { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import { formatClaveDePredio } from 'views/Catastro/utils/onBlurKey';

import { padronesEnum } from 'views/Tramites/GPM/enums';
import { DECIMAL_PATTERN, toFixed } from 'utils/normalizers';

import { getParticipes } from './participes';

export const baseURI = 'catastro/predios/';
export const deletableKeysPredio = [
  'json_predio',
  'lote_iprovinay',
  'manzana_iprovinay',
  'desarrollo_de_condominio',
  'numero_de_depto',
  'regimen',
  'edificio',
  'zona_de_predio',
  'andador',
  'barrio_paraje',
  'fraccion',
  'casa',
  'corredor_urbano',
  'observaciones',
  'notas',
  // datos modificables por el participe principal
  'apellido_paterno_propietario',
  'apellido_materno_propietario',
  'ciudadano',
  'contribuyente',
];

export const dateKeysPredio = [
  'fecha_asegurado',
  'fecha_registro_publico',
  'fecha_estado',
  'fecha_de_escritura',
  'fecha_visita',
  'fecha_adjudicacion_traslado',
  // 'fecha_de_celebracion',
];

export const formatValoresPredio = (predio) => ({
  superficie_total_del_terreno: toFixed(predio.superficie_total_del_terreno || 0, 3),
  valor_del_terreno: toFixed(predio.valor_del_terreno || 0, 3),
  area_titulo: toFixed(predio.area_titulo || 0, 3),
  superficie_de_la_construccion: toFixed(predio.superficie_de_la_construccion || 0, 3),
  valor_de_la_construccion: toFixed(predio.valor_de_la_construccion || 0, 3),
  valor_catastral: toFixed(predio.valor_catastral || 0, 3),
  tasa: toFixed(predio.tasa || 0, 6),
  frente: toFixed(predio.frente || 0, 2),
  indiviso: toFixed(predio.indiviso || 0, 8),
  profundidad: toFixed(predio.profundidad || 0, 2),
  perimetro: toFixed(predio.perimetro || 0, 2),
  factor_merito_demerito: toFixed(predio.factor_merito_demerito || 0, 2),
  porcentaje_demerito: toFixed(predio.porcentaje_demerito || 0, 4),
  valor_catastral_calculado: toFixed(predio.valor_catastral_calculado || 0, 3),
  porcentaje_traslado: toFixed(predio.porcentaje_traslado || 0, 2),
});

const formatPredio = (p) => {
  let json_predio;
  if (p?.json_predio) {
    json_predio = JSON.parse(p.json_predio);
    Object.entries(json_predio).forEach(([key, value]) => {
      json_predio[key] = !Array.isArray(value) && DECIMAL_PATTERN.test(value)
        ? parseFloat(value) : value;
    });
    const formatedValuesJson = formatValoresPredio(json_predio);
    json_predio = formatReceived({ ...json_predio, ...formatedValuesJson });
    return {
      ...formatReceived(p),
      json_predio,
      json_predio_raw: p.json_predio,
    };
  }
  return p;
};

/**
 * GET Predio
 * @param {Object} params
 * @returns {Promise<Array<object>>}
 */
export const getPredios = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    response.data.results = response.data.results.map((p) => formatPredio(formatReceived(p)));
    return response.data;
  } catch (err) {
    onError(err);
    return { results: [] };
  }
};

/**
 * GET Predio
 * @param {number} id
 * @returns {Promise<object>}
 */
export const getPredio = async (id, indexParticipes = true) => {
  try {
    const response = await API.get(`${baseURI}${id}`);
    if (response.data.notario_manual) {
      response.data.notario_predio = 0;
    }
    if (indexParticipes) {
      response.data.participes = await getParticipes({ predio: id });
    }
    return formatPredio(formatReceived(response.data));
  } catch (err) {
    onError(err);
    return null;
  }
};
/**
 *
 * @param {object} values
 *
 * @returns {Promise<object|boolean>}
 */
export const postPredio = async (_values, clean = true, accion = 'PREDIO') => {
  try {
    const values = getFormattedValues(
      _values,
      { clean, deletableKeys: deletableKeysPredio },
      dateKeysPredio,
    );
    const accionInf = ensurePrefixed('CREAR_', accion);

    values.objeto_cuenta = formatClaveDePredio(values.objeto_cuenta);
    values.clave = formatClaveDePredio(values.clave);
    if (values.notario_predio === 0) {
      values.notario_predio = null;
    }
    if (values.cleanJson) {
      values.json_predio = '';
      delete values.cleanJson;
    }
    if (values.contribuyente) {
      values.ciudadano = null;
    }
    if (values.ciudadano) {
      values.contribuyente = null;
    }

    values.descripcion = values.descripcion || 'General';
    const response = await API.post(baseURI, values, {
      accionInf,
      id_padron: padronesEnum.PREDIO,
      clave: values.clave,
    });
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

export const patchPredio = async (id, _values, clean = true, accion = 'PREDIO') => {
  try {
    const values = getFormattedValues(
      _values,
      { clean, deletableKeys: deletableKeysPredio },
      dateKeysPredio,
    );
    let accionFinal = accion;
    if (values.direccion) {
      accionFinal = 'DIRECCION_PREDIO';
    }
    if (values.json_predio) {
      accionFinal = 'PREDIO_TRAMITE';
    }
    values.objeto_cuenta = formatClaveDePredio(values.objeto_cuenta);
    values.clave = formatClaveDePredio(values.clave);

    if (values.notario_predio === 0) {
      values.notario_predio = null;
    }
    if (values.cleanJson) {
      values.json_predio = '';
      delete values.cleanJson;
      accionFinal = 'CANCELAR_TRAMITE_PREDIO';
    }
    if (values.contribuyente) {
      values.ciudadano = null;
    }
    if (values.ciudadano) {
      values.contribuyente = null;
    }
    const accionInf = ensurePrefixed('EDITAR_', accionFinal);
    const response = await API.patch(`${baseURI}${id}`, values, {
      accionInf,
      id_padron: padronesEnum.PREDIO,
      clave: values.clave,
    });
    onSuccess(response, 'Editado correctamente');
    return formatPredio(response.data);
  } catch (err) {
    onError(err);
    return false;
  }
};

export const deletePredio = async (id) => {
  try {
    const response = await API.delete(`${baseURI}${id}`);
    onSuccess(response, 'Editado correctamente');
    return true;
  } catch (err) {
    onError(err);
    return false;
  }
};
/**
 * Aplica afectaciones de participes, zonas_homogeneas_de_predios, construcciones
 * colindancias y meritos_demeritos_de_predios
 */
export const aplicarAfectacionesPredio = async (values) => {
  try {
    const response = await API.post('catastro/participe/aplicar-afectaciones', values);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};
